import React, { useRef, useState, useEffect } from 'react';

import { ChevronDownIcon } from '@chakra-ui/icons';
import { Flex, Box, Button, Link, Text } from '@chakra-ui/react';

import { DefaultMarketplaceMenuContent } from './NewNavigationMenuContent';
import { useIsMobile } from '../../../hooks';
import { useGetLiveLoginLink } from '../../../hooks/useGetLiveLoginLink';
import { ExternalLinkIcon } from '../../../Icons/ExternalLinkIcon';
import { useModalContext } from '../../../ModalProvider';
import { tracker } from '../../../utilities/tracker';
import { getLink } from '../../utils/monolithUrls';

export const NewNavNavigation = ({ top = 0 }: { top: number }) => {
  const { fanaticsCollectBaseUrl } = useModalContext();
  const fanaticsLiveUrl = useGetLiveLoginLink();
  const isLg = useIsMobile('lg');
  const [isAuctionMenuVisible, setIsAuctionMenuVisible] = useState(false);
  const [isBuyNowMenuVisible, setIsBuyNowMenuVisible] = useState(false);
  const [isPremierMenuVisible, setIsPremierMenuVisible] = useState(false);
  const [isTouchEvent, setIsTouchEvent] = useState(false);
  const auctionsTriggerRef = useRef<HTMLButtonElement>(null);
  const buyNowTriggerRef = useRef<HTMLButtonElement>(null);
  const auctionTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const buyNowTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const premierTriggerRef = useRef<HTMLButtonElement>(null);
  const premierTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const frameRef = useRef<number>();

  const track = (sectionClicked: string) => {
    tracker.track('Navigation Click', {
      replatformed: false,
      sectionClicked,
    });
  };

  const handleTriggerClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    link: string,
    title: string
  ) => {
    track(title);
    if (!isTouchEvent) {
      window.location.href = link;
    }
    // Cancel any existing frame
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }
    // Store the new frame ID
    frameRef.current = requestAnimationFrame(() => {
      setIsTouchEvent(false);
    });
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  const handleTriggerTouchStart = (
    e: React.TouchEvent<HTMLButtonElement>,
    type: 'auctions' | 'buyNow' | 'premier'
  ) => {
    e.preventDefault();
    setIsTouchEvent(true);

    if (type === 'auctions') {
      setIsAuctionMenuVisible(!isAuctionMenuVisible);
    } else if (type === 'buyNow') {
      setIsBuyNowMenuVisible(!isBuyNowMenuVisible);
    } else if (type === 'premier') {
      setIsPremierMenuVisible(!isPremierMenuVisible);
    }
  };

  const handleCloseMenu = (type: 'auctions' | 'buyNow' | 'premier') => {
    if (type === 'auctions') {
      setIsAuctionMenuVisible(false);
    } else if (type === 'buyNow') {
      setIsBuyNowMenuVisible(false);
    } else if (type === 'premier') {
      setIsPremierMenuVisible(false);
    }
  };

  const trackAndClose = (
    type: 'auctions' | 'buyNow' | 'premier',
    title: string
  ) => {
    track(title);
    handleCloseMenu(type);
  };

  return (
    <Flex alignItems="center" gap={{ base: '8px', md: '16px', xl: '32px' }}>
      {/* Buy Now */}
      <Box
        position="relative"
        onMouseEnter={() => {
          clearTimeout(buyNowTimeoutRef.current);
          setIsBuyNowMenuVisible(true);
        }}
        onMouseLeave={() => {
          buyNowTimeoutRef.current = setTimeout(() => {
            setIsBuyNowMenuVisible(false);
          }, 300);
        }}
      >
        <Link
          _hover={{ textDecoration: 'none' }}
          display="block"
          href={getLink(fanaticsCollectBaseUrl, 'fixedSRP')}
          onClick={(e) => e.preventDefault()}
        >
          <Button
            _hover={{ backgroundColor: 'transparent' }}
            alignItems="center"
            backgroundColor="transparent"
            border="none"
            borderRadius="0"
            display="flex"
            fontSize="16px"
            fontWeight="bold"
            gap="4px"
            height="auto"
            padding={0}
            ref={buyNowTriggerRef}
            textTransform="none"
            whiteSpace="nowrap"
            onClick={(e) =>
              handleTriggerClick(
                e,
                getLink(fanaticsCollectBaseUrl, 'fixedSRP'),
                'Buy Now'
              )
            }
            onTouchStart={(e) => handleTriggerTouchStart(e, 'buyNow')}
          >
            Buy Now
            <ChevronDownIcon height="16px" width="16px" />
          </Button>
        </Link>
        {isBuyNowMenuVisible && (
          <Box
            backgroundColor="white"
            boxShadow="md"
            left="0"
            position="fixed"
            top={`${top}px`}
            width="100%"
            zIndex={1051}
          >
            <DefaultMarketplaceMenuContent
              trackAndClose={(title) => trackAndClose('buyNow', title)}
              type="buyNow"
            />
          </Box>
        )}
      </Box>

      {/* Auction */}
      <Box
        position="relative"
        onMouseEnter={() => {
          clearTimeout(auctionTimeoutRef.current);
          setIsAuctionMenuVisible(true);
        }}
        onMouseLeave={() => {
          auctionTimeoutRef.current = setTimeout(() => {
            setIsAuctionMenuVisible(false);
          }, 300);
        }}
      >
        <Link
          _hover={{ textDecoration: 'none' }}
          display="block"
          href={getLink(fanaticsCollectBaseUrl, 'weeklySRP')}
          onClick={(e) => e.preventDefault()}
        >
          <Button
            _hover={{ backgroundColor: 'transparent' }}
            alignItems="center"
            backgroundColor="transparent"
            border="none"
            borderRadius="0"
            display="flex"
            fontSize="16px"
            fontWeight="bold"
            gap="4px"
            height="auto"
            padding={0}
            ref={auctionsTriggerRef}
            textTransform="none"
            whiteSpace="nowrap"
            onClick={(e) =>
              handleTriggerClick(
                e,
                getLink(fanaticsCollectBaseUrl, 'weeklySRP'),
                'Auction'
              )
            }
            onTouchStart={(e) => handleTriggerTouchStart(e, 'auctions')}
          >
            Auction
            <ChevronDownIcon height="16px" width="16px" />
          </Button>
        </Link>
        {isAuctionMenuVisible && (
          <Box
            backgroundColor="white"
            boxShadow="md"
            left="0"
            position="fixed"
            top={`${top}px`}
            width="100%"
            zIndex={1051}
          >
            <DefaultMarketplaceMenuContent
              trackAndClose={(title) => trackAndClose('auctions', title)}
              type="auctions"
            />
          </Box>
        )}
      </Box>

      {/* Premier Auction */}
      <Box
        position="relative"
        onMouseEnter={() => {
          clearTimeout(premierTimeoutRef.current);
          setIsPremierMenuVisible(true);
        }}
        onMouseLeave={() => {
          premierTimeoutRef.current = setTimeout(() => {
            setIsPremierMenuVisible(false);
          }, 300);
        }}
      >
        <Link
          _hover={{ textDecoration: 'none' }}
          display="block"
          href={getLink(fanaticsCollectBaseUrl, 'premierSRP')}
          onClick={(e) => e.preventDefault()}
        >
          <Button
            _hover={{ backgroundColor: 'transparent' }}
            alignItems="center"
            backgroundColor="transparent"
            border="none"
            borderRadius="0"
            display="flex"
            fontSize="16px"
            fontWeight="bold"
            gap="4px"
            height="auto"
            padding={0}
            ref={premierTriggerRef}
            textTransform="none"
            whiteSpace="nowrap"
            onClick={(e) =>
              handleTriggerClick(
                e,
                getLink(fanaticsCollectBaseUrl, 'premierSRP'),
                'Premier Auction'
              )
            }
            onTouchStart={(e) => handleTriggerTouchStart(e, 'premier')}
          >
            Premier Auction
            <ChevronDownIcon height="16px" width="16px" />
          </Button>
        </Link>
        {isPremierMenuVisible && (
          <Box
            backgroundColor="white"
            boxShadow="md"
            left="0"
            position="fixed"
            top={`${top}px`}
            width="100%"
            zIndex={1051}
          >
            <DefaultMarketplaceMenuContent
              trackAndClose={(title) => trackAndClose('premier', title)}
              type="premier"
            />
          </Box>
        )}
      </Box>

      {/* Live Breaking Link - only on larger screens */}
      {!isLg && (
        <Link
          _hover={{ textDecoration: 'none' }}
          alignItems="center"
          display="flex"
          gap="4px"
          href={fanaticsLiveUrl}
          target="_blank"
          onClick={() => track('Live')}
        >
          <Text fontSize="16px" fontWeight="bold" whiteSpace="nowrap">
            Live Breaking
          </Text>
          <ExternalLinkIcon height="16px" width="16px" />
        </Link>
      )}
    </Flex>
  );
};
